import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useDebounce } from '../../hooks/useDebounce';

export const Route = createFileRoute('/qti-embed/')({
  component: QtiEmbedInstructionsPage,
  ssr: false,
});

type QTIEmbedSearchParams = {
  itemId?: string;
};

const route = getRouteApi('/qti-embed/');

const DEFAULT_QTI_ID = 'qti-e9047b80-9a67-4167-8d06-9aad3163b609';

function QtiEmbedInstructionsPage() {
  const params = route.useSearch() as QTIEmbedSearchParams;
  const [qtiId, setQtiId] = useState(params.itemId || DEFAULT_QTI_ID);
  const [responseValue, setResponseValue] = useState('choice_2');
  const navigate = route.useNavigate();

  const baseUrl = window.location.origin;
  const iframeCode = qtiId
    ? `<iframe
  src="${baseUrl}/qti-embed/${qtiId}"
  id="qti-embed"
  width="100%"
  height="600px"
  frameborder="0"
></iframe>`
    : '';

  useEffect(() => {
    if (window) {
      window.addEventListener('message', (event) => {
        // Check for QTI response data
        if (event.data && event.data.type === 'QTI_RESPONSE_CHANGE') {
          const {
            responseIdentifier: _responseIdentifier,
            response: _response,
          } = event.data;
        }
      });
    }
  }, []);

  const handleCopyIframeCode = () => {
    navigator.clipboard.writeText(iframeCode);
  };

  const handleSimulateSetResponse = () => {
    const qtiIframe = document.querySelector('iframe');
    if (qtiIframe) {
      qtiIframe.contentWindow?.postMessage(
        {
          type: 'QTI_SET_RESPONSE',
          responseIdentifier: 'RESPONSE',
          response: responseValue,
        },
        '*'
      );
    }
  };

  const handleSimulateFeedback = (displayResponseFeedback: boolean) => {
    const qtiIframe = document.querySelector('iframe');
    if (qtiIframe) {
      qtiIframe.contentWindow?.postMessage(
        {
          type: 'QTI_DISPLAY_FEEDBACK',
          displayResponseFeedback,
        },
        '*'
      );
    }
  };

  const debouncedQtiId = useDebounce(qtiId, 500);

  useEffect(() => {
    if (debouncedQtiId && debouncedQtiId !== params.itemId) {
      navigate({
        search: { itemId: debouncedQtiId },
      });
    }
  }, [debouncedQtiId, params.itemId, navigate]);

  const handleQtiIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQtiId(e.target.value);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">QTI Item Embed Instructions</h1>

      <div className="flex gap-6">
        {/* Left Sidebar with Controls */}
        <div className="w-1/3 space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-3">How to Use</h2>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Enter your QTI Item ID in the input field below</li>
              <li>Copy the generated iframe code</li>
              <li>Paste the iframe code into your HTML page</li>
            </ol>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">QTI Item ID</h2>
            <div className="flex gap-2">
              <input
                type="text"
                value={qtiId}
                onChange={handleQtiIdChange}
                placeholder="Enter QTI Item ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">Simulation Controls</h2>
            <div className="space-y-3">
              <div>
                <input
                  type="text"
                  value={responseValue}
                  onChange={(e) => setResponseValue(e.target.value)}
                  placeholder="Response value (e.g. choice_a)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                />
                <button
                  onClick={handleSimulateSetResponse}
                  className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 cursor-pointer"
                >
                  Simulate "Set Response" Message
                </button>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => handleSimulateFeedback(true)}
                  className="w-full px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 cursor-pointer"
                >
                  Display Response Feedback = true
                </button>
                <button
                  onClick={() => handleSimulateFeedback(false)}
                  className="w-full px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 cursor-pointer"
                >
                  Display Response Feedback = false
                </button>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">
              Listening for Response Changes
            </h2>
            <p className="mb-2">
              To listen for response changes in the embedded QTI item, add an
              event listener to your page:
            </p>
            <pre className="bg-gray-50 p-3 rounded-md font-mono text-sm overflow-x-auto mb-2">
              {`window.addEventListener('message', (event) => {
  // Check for QTI response data
  if (event.data && event.data.type === 'QTI_RESPONSE_CHANGE') {
    const { responseIdentifier, response } = event.data;

  }
});`}
            </pre>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">Setting a Response</h2>
            <p className="mb-2">
              To programmatically set a response in the embedded QTI item:
            </p>
            <pre className="bg-gray-50 p-3 rounded-md font-mono text-sm overflow-x-auto mb-2">
              {`// Get a reference to the iframe
const qtiIframe = document.querySelector('#qti-embed');

// Set a response in the iframe
qtiIframe.contentWindow.postMessage({
  type: 'QTI_SET_RESPONSE',
  responseIdentifier: "RESPONSE",
  response: "choice_a"
}, '*');`}
            </pre>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">
              Displaying Response Feedback
            </h2>
            <p className="mb-2">
              To display feedback in the embedded QTI item:
            </p>
            <pre className="bg-gray-50 p-3 rounded-md font-mono text-sm overflow-x-auto mb-2">
              {`// Get a reference to the iframe
const qtiIframe = document.querySelector('#qti-embed');

// Show feedback in the iframe
qtiIframe.contentWindow.postMessage({
  type: 'QTI_DISPLAY_FEEDBACK',
  displayResponseFeedback: true
}, '*');`}
            </pre>
          </div>
        </div>

        {/* Main Area with Preview and Code */}
        <div className="w-2/3 space-y-6">
          {qtiId && (
            <div>
              <h2 className="text-xl font-semibold mb-3">
                Generated Embed Code
              </h2>
              <div className="relative">
                <textarea
                  readOnly
                  value={iframeCode}
                  className="w-full h-32 p-3 border border-gray-300 rounded-md font-mono text-sm bg-gray-50"
                />
                <button
                  onClick={handleCopyIframeCode}
                  className="absolute top-2 right-2 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {qtiId && (
            <div>
              <h2 className="text-xl font-semibold mb-3">Preview</h2>
              <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                <iframe
                  src={`/qti-embed/${qtiId}`}
                  width="100%"
                  height="600"
                  title="QTI Item Preview"
                  className="border-none"
                />
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Use the controls in the sidebar to simulate response changes and
                feedback display.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
